import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { useDataTableState } from "@trillion/ui";
import { Input } from "@trillion/ui/components/ui/input";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Search, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin-api";
import { UserStatus } from "@trillion/enums";
import { getUserColumns } from "@/components/users/user-columns";
import type { User } from "@/types";

const PAGE_SIZE = 20;

export function UsersPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");

  const t = useDataTableState<User>({
    fetcher: async (p) => {
      const res = await adminApi.users.list({
        page: p.page,
        pageSize: p.pageSize,
        search: p.search || undefined,
        userStatusId: p.filters.userStatusId as UserStatus | undefined,
      });
      return { items: res.items, totalCount: res.total_count };
    },
    // The "all" sentinel stays a string (Radix Select is string-valued); it converts here.
    filters: { userStatusId: statusFilter !== "all" ? Number(statusFilter) : undefined },
    pageSize: PAGE_SIZE,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to load users"),
  });

  // No refetch after either action: neither changes a column this grid renders (the new expires_date
  // isn't shown), so `t` stays out of the dependency list and the identities stay stable.
  const handleResendInvite = useCallback(async (user: User) => {
    try {
      await adminApi.users.resendInvite(user.user_id);
      toast.success(`Invite re-sent to ${user.email}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend the invite.");
    }
  }, []);

  const handleResendVerification = useCallback(async (user: User) => {
    try {
      await adminApi.users.resendVerification(user.user_id);
      toast.success(`Verification email sent to ${user.email}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send the verification email.");
    }
  }, []);

  const columns = useMemo(
    () =>
      getUserColumns(
        (userId) => navigate(`/users/${userId}`),
        handleResendInvite,
        handleResendVerification
      ),
    [navigate, handleResendInvite, handleResendVerification]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const toolbar = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users by email or name..."
          value={t.search}
          onChange={(e) => t.setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Filter by status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value={String(UserStatus.Active)}>Active</SelectItem>
          <SelectItem value={String(UserStatus.Invited)}>Invited</SelectItem>
          <SelectItem value={String(UserStatus.Suspended)}>Suspended</SelectItem>
          <SelectItem value={String(UserStatus.Deleted)}>Deleted</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">
        {t.totalCount > 0
          ? `Showing ${(t.page - 1) * PAGE_SIZE + 1}-${Math.min(
              t.page * PAGE_SIZE,
              t.totalCount
            )} of ${t.totalCount} users`
          : "No users"}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => t.setPage(Math.max(1, t.page - 1))}
          disabled={t.page <= 1}
        >
          <ChevronLeft className="size-4" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Page {t.page} of {t.totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => t.setPage(t.page + 1)}
          disabled={t.page >= t.totalPages}
        >
          Next
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Users"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Users" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Users"
          description="Manage user accounts across the Passport identity system."
          actions={
            <Button onClick={() => navigate("/users/invite")}>
              <UserPlus className="mr-2 size-4" />
              Invite user
            </Button>
          }
        />

        <DataTableWrapper
          title="All Users"
          description={
            t.totalCount > 0
              ? `${t.totalCount} user${t.totalCount !== 1 ? "s" : ""} total`
              : undefined
          }
          toolbar={toolbar}
          pagination={pagination}
          isLoading={t.isLoading}
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableWrapper>
      </div>
    </div>
  );
}
